/**
 * Detect whether the browser can run MapLibre/WebGL map engines without
 * freezing. Software GL (llvmpipe / SwiftShader / Softpipe) "succeeds" at
 * context creation but stalls for seconds on every frame (ReadPixels) —
 * that is the 4–10s zoom freeze on GPU-less VMs.
 */

export type WebGlCapability = {
  ok: boolean;
  kind: 'webgl2' | 'webgl' | null;
  vendor: string | null;
  renderer: string | null;
  software: boolean;
  reason?: string;
};

const SOFTWARE_GL_RE =
  /llvmpipe|swiftshader|softpipe|software rasterizer|microsoft basic render|mesa offscreen|virtualbox|google swiftshader/i;

export function isSoftwareWebGlRenderer(renderer: string | null | undefined): boolean {
  if (!renderer) return false;
  return SOFTWARE_GL_RE.test(renderer);
}

export function probeWebGlCapability(): WebGlCapability {
  if (typeof document === 'undefined') {
    return { ok: false, kind: null, vendor: null, renderer: null, software: true, reason: 'no-document' };
  }

  const canvas = document.createElement('canvas');
  canvas.width = 1;
  canvas.height = 1;

  // Reject major performance caveats (software GL) when the browser supports it.
  const strictAttrs: WebGLContextAttributes = {
    alpha: true,
    depth: true,
    stencil: false,
    antialias: false,
    preserveDrawingBuffer: false,
    failIfMajorPerformanceCaveat: true,
    powerPreference: 'high-performance',
  };

  let gl: WebGLRenderingContext | WebGL2RenderingContext | null = null;
  let kind: 'webgl2' | 'webgl' | null = null;
  try {
    gl = canvas.getContext('webgl2', strictAttrs) as WebGL2RenderingContext | null;
    if (gl) kind = 'webgl2';
    else {
      gl = canvas.getContext('webgl', strictAttrs) as WebGLRenderingContext | null;
      if (gl) kind = 'webgl';
    }
  } catch (err) {
    return {
      ok: false,
      kind: null,
      vendor: null,
      renderer: null,
      software: true,
      reason: err instanceof Error ? err.message : String(err),
    };
  }

  if (!gl) {
    // Strict probe failed — try a loose probe only to classify the renderer.
    let loose: WebGLRenderingContext | null = null;
    try {
      loose = canvas.getContext('webgl', {
        ...strictAttrs,
        failIfMajorPerformanceCaveat: false,
      }) as WebGLRenderingContext | null;
    } catch {
      loose = null;
    }
    if (!loose) {
      return { ok: false, kind: null, vendor: null, renderer: null, software: true, reason: 'no-webgl' };
    }
    const dbg = loose.getExtension('WEBGL_debug_renderer_info');
    const vendor = dbg ? String(loose.getParameter(dbg.UNMASKED_VENDOR_WEBGL)) : null;
    const renderer = dbg ? String(loose.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : null;
    loose.getExtension('WEBGL_lose_context')?.loseContext();
    return {
      ok: false,
      kind: 'webgl',
      vendor,
      renderer,
      software: true,
      reason: isSoftwareWebGlRenderer(renderer)
        ? `software-gl:${renderer}`
        : 'major-performance-caveat',
    };
  }

  const dbg = gl.getExtension('WEBGL_debug_renderer_info');
  const vendor = dbg ? String(gl.getParameter(dbg.UNMASKED_VENDOR_WEBGL)) : null;
  const renderer = dbg ? String(gl.getParameter(dbg.UNMASKED_RENDERER_WEBGL)) : null;
  const software = isSoftwareWebGlRenderer(renderer);
  gl.getExtension('WEBGL_lose_context')?.loseContext();

  if (software) {
    return {
      ok: false,
      kind,
      vendor,
      renderer,
      software: true,
      reason: `software-gl:${renderer}`,
    };
  }

  return { ok: true, kind, vendor, renderer, software: false };
}

/** Throw a WebGL-tagged error so TwinShell switches to Canvas 2D. */
export function assertHardwareWebGlForMapLibre(): WebGlCapability {
  const cap = probeWebGlCapability();
  if (!cap.ok) {
    const detail = cap.reason ?? cap.renderer ?? 'unavailable';
    throw new Error(
      `WebGL unsuitable for MapLibre (${detail}). `
      + 'Use Canvas 2D / OpenLayers on this host — software WebGL freezes the UI.',
    );
  }
  return cap;
}

/**
 * Cached verdicts. Probing creates and discards a WebGL context, so the result
 * is memoised for the session — callers ask on every engine decision.
 *
 * The three answers are deliberately different questions:
 *   - `hasHardwareWebGl` — GPU-accelerated. MapLibre requires this.
 *   - `canRunWebGlEngines` — a context exists at all, accelerated or not. 3D
 *     content has no Canvas 2D equivalent, so a slow SwiftShader / Basic
 *     Render Driver scene still beats refusing to render it.
 *   - `isSoftwareWebGlSession` — available but not accelerated, i.e. the case
 *     where the UI should warn that 3D will be slow.
 */
let cachedCapability: WebGlCapability | null = null;

function capability(): WebGlCapability {
  cachedCapability ??= probeWebGlCapability();
  return cachedCapability;
}

export function hasHardwareWebGl(): boolean {
  return capability().ok;
}

export function canRunWebGlEngines(): boolean {
  return capability().kind !== null;
}

export function isSoftwareWebGlSession(): boolean {
  const cap = capability();
  return cap.kind !== null && !cap.ok;
}

export function shouldPreferCanvasMapEngine(): boolean {
  if (typeof window === 'undefined') return false;
  try {
    if (window.sessionStorage.getItem('geoviewer.canvasFallback') === '1') return true;
  } catch {
    /* ignore */
  }
  const cap = probeWebGlCapability();
  if (!cap.ok) {
    try {
      window.sessionStorage.setItem('geoviewer.canvasFallback', '1');
    } catch {
      /* ignore */
    }
    return true;
  }
  return false;
}
